import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useChatContext } from '@/contexts/chat/context';
import { useSessionContext } from '@/contexts/session/context';
import { GroupStatus } from '@/configs/apis/groups/interface';
import { useRefs } from '@/scripts/hooks';
import ChatInput from '../chat-input';
import classes from './index.module.scss';
import InfoIcon from './images/info.svg?react';

export interface ChatFooterProps {
  className?: ClassNames;
  chat: Chat;
}

function GroupError({ desc }: { desc: string }) {
  return (
    <div className={classes.groupError}>
      <div className={classes.groupErrorDesc}>
        <InfoIcon />
        {desc}
      </div>
    </div>
  );
}

export default function ChatFooter({ className, chat }: ChatFooterProps) {
  const {
    sendMessage,
    //updateStateMessageAsRead,
    chatGroup: {
      chatGroupState: {
        state: { membersMap, groupMap },
      },
    },
    chatsState: { activatedChat },
    singleChatsValue: { syncSingleChat },
    groupChatsValue: { syncGroupSettings },
    getLatestGroupInfo,
    getLatestGroupMemberList,
    updateChats,
  } = useChatContext();
  const { session } = useSessionContext();

  const normalMemberList = useMemo(
    () => membersMap[chat.chat_id]?.normalMembers,
    [membersMap, chat.chat_id]
  );

  const group = useMemo(() => groupMap[chat.chat_id], [groupMap, chat.chat_id]);

  const isGroup = useMemo(() => chat.category === ChatCategory.GROUP, [chat.category]);

  const isGroupLeave = useMemo(() => {
    const inGroup = normalMemberList?.some(
      (member) => member.user_id === session?.user?.userId
    );
    return isGroup && !inGroup;
  }, [isGroup, normalMemberList, session?.user?.userId]);

  const isGroupDisband = useMemo(() => {
    return isGroup && group?.status === GroupStatus.DISBAND;
  }, [isGroup, group]);

  const refs = useRefs({
    getLatestGroupInfo,
    getLatestGroupMemberList,
    isGroup,
    syncGroupSettings,
    syncSingleChat,
    updateChats,
  });

  useEffect(() => {
    const {
      getLatestGroupInfo,
      getLatestGroupMemberList,
      isGroup,
      syncGroupSettings,
      syncSingleChat,
    } = refs.current;
    if (isGroup) {
      syncGroupSettings(chat.chat_id);
      getLatestGroupInfo(chat.chat_id);
      getLatestGroupMemberList(chat.chat_id);
    } else {
      syncSingleChat(chat.chat_id);
    }
  }, [refs, chat.chat_id]);

  // if (chat.loading) {
  //   return null;
  // }

  if (isGroupDisband) {
    return <GroupError desc="You can’t message a disbanded group." />;
  }

  if (isGroupLeave) {
    return <GroupError desc="Unable to send message in an exited group chat" />;
  }
  return (
    <div className={cls(classes.chatFooter, className)}>
      <ChatInput
        chat={chat}
        activatedChat={activatedChat}
        updateChats={refs.current.updateChats}
        onSend={(message) => {
          sendMessage({
            chat,
            message,
          });
          //updateStateMessageAsRead(chat);
        }}
      />
    </div>
  );
}
